import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { FacilitiesTable } from './FacilitiesTable';
import { OwnersTable } from './OwnersTable';
import ExportSchemePDFsDialog from './ExportSchemePDFsDialog';
import api from '../../api/axios';

interface InvestigativeToolsProps {
  // Case info
  caseId: string;

  // Facilities props
  facilities: any[];
  facilitiesWithSchemeData: any[];
  facilitiesData: any;
  facilitiesLoading: boolean;
  schemeSummaryLoading: boolean;
  facilitiesSearch: string;
  facilitiesStateFilter: string;
  facilitiesPage: number;
  facilitiesPageSize: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  expandedFacilitiesScheme: Set<string>;
  expandedSchemeInstances: Set<string>;
  highlightedOwner: string | null;
  states: any[];
  ownershipChangesData: any;
  onSearchChange: (value: string) => void;
  onStateFilterChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (field: string) => void;
  onFacilitySchemeExpand: (ccn: string) => void;
  onSchemeInstanceExpand: (instanceKey: string) => void;
  onOwnerPillClick: (ownerName: string) => void;
  onAddFacility: () => void;
  onDeleteFacility: (facility: any) => void;

  // Owners/Defendants props
  owners: any[];
  expandedOwners: Set<string>;
  onOwnerExpand: (ownerId: string) => void;

  // Shared
  data: any;
  formatMoney: (amount: number) => string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`investigative-tools-tabpanel-${index}`}
      aria-labelledby={`investigative-tools-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export const InvestigativeTools: React.FC<InvestigativeToolsProps> = ({
  caseId,
  facilities,
  facilitiesWithSchemeData,
  facilitiesData,
  facilitiesLoading,
  schemeSummaryLoading,
  facilitiesSearch,
  facilitiesStateFilter,
  facilitiesPage,
  facilitiesPageSize,
  sortField,
  sortDirection,
  expandedFacilitiesScheme,
  expandedSchemeInstances,
  highlightedOwner,
  states,
  ownershipChangesData,
  onSearchChange,
  onStateFilterChange,
  onPageChange,
  onPageSizeChange,
  onSort,
  onFacilitySchemeExpand,
  onSchemeInstanceExpand,
  onOwnerPillClick,
  onAddFacility,
  onDeleteFacility,
  owners,
  expandedOwners,
  onOwnerExpand,
  data,
  formatMoney
}) => {
  const [subTabValue, setSubTabValue] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSubTabValue(newValue);
  };

  const pollTaskStatus = async (taskId: string): Promise<any> => {
    try {
      const { data } = await api.get(`/celery-tasks/${taskId}/`);
      return data;
    } catch (err) {
      console.error('Failed to poll task status:', err);
      return { state: 'FAILURE' };
    }
  };

  const downloadFile = async (downloadKey: string) => {
    try {
      // Use the docgen download endpoint with the storage key as a query parameter
      const response = await api.get(`/docgen/${caseId}/download/`, {
        params: { key: downloadKey },
        responseType: 'blob'
      });

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `scheme_pdfs_${Date.now()}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download file:', err);
      alert('Failed to download ZIP file. Please try again.');
    }
  };

  const handleExportConfirm = async () => {
    setExporting(true);

    try {
      // Start the export task
      const response = await api.post(`/cases/${caseId}/export_facility_scheme_pdfs/`);
      const taskId = response.data.task_id;

      // Poll for completion
      const poll = async () => {
        const taskData = await pollTaskStatus(taskId);

        if (taskData.state === 'SUCCESS') {
          setExporting(false);
          setExportDialogOpen(false);

          // Download the file
          if (taskData.result?.download_key) {
            await downloadFile(taskData.result.download_key);
          } else {
            alert('PDF generation completed but no download link was provided.');
          }
        } else if (taskData.state === 'FAILURE') {
          setExporting(false);
          alert('PDF generation failed. Please try again.');
        } else if (['PENDING', 'STARTED', 'PROGRESS', 'RETRY'].includes(taskData.state)) {
          // Continue polling
          setTimeout(poll, 2000);
        }
      };

      poll();
    } catch (err: any) {
      console.error('Export failed:', err);
      setExporting(false);
      alert(err.response?.data?.detail || 'Failed to start PDF export. Please try again.');
    }
  };

  // Count facilities with scheme data
  const facilitiesWithSchemes = facilitiesWithSchemeData?.length || 0;

  return (
    <Box>
      {/* Scheme-Specific Metrics Header */}
      <Paper sx={{ p: 2.5, mb: 3, backgroundColor: 'grey.50' }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, mb: 2, display: 'block' }}>
          Scheme Summary
        </Typography>

        {schemeSummaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Metrics Row */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 2,
                borderBottom: 1,
                borderColor: 'divider',
                mb: 2
              }}
            >
              {/* Left Metrics */}
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1, mb: 0.5 }}>
                    {data.summary?.total_facilities || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Facilities
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1, mb: 0.5 }}>
                    {data.summary?.total_owners || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Defendants
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color:
                        data?.scheme_instance_summary?.total_count > 0 ? 'warning.main' : 'text.disabled',
                      lineHeight: 1,
                      mb: 0.5
                    }}
                  >
                    {data?.scheme_instance_summary?.scheme_matched ?? '-'}
                    {data?.scheme_instance_summary?.total_count > 0 &&
                      data?.scheme_instance_summary?.scheme_matched !==
                        data?.scheme_instance_summary?.total_count && (
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: 0.5, fontWeight: 400 }}
                        >
                          / {data?.scheme_instance_summary?.total_count}
                        </Typography>
                      )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Scheme Instances
                  </Typography>
                </Box>
              </Box>

              {/* Right Financial - Billed Only */}
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1, mb: 0.5 }}>
                  {formatMoney(data.summary?.total_billed)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Billed
                </Typography>
              </Box>
            </Box>

            {/* Bottom Row - States and Paid Amount */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mr: 0.5 }}>
                  States:
                </Typography>
                {data.summary?.states?.map((stateData: any) => (
                  <Chip
                    key={stateData.state}
                    label={`${stateData.state} (${stateData.count})`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem', height: '22px' }}
                  />
                ))}
              </Stack>

              {/* Total Paid */}
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1, mb: 0.5 }}>
                  {formatMoney(data.summary?.total_paid)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total Paid
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Sub-tabs for Facilities and Defendants */}
      <Paper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs
            value={subTabValue}
            onChange={handleSubTabChange}
            sx={{ flexGrow: 1 }}
          >
            <Tab label="Facilities" />
            <Tab label="Defendants" />
          </Tabs>

          {/* Export PDFs Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => setExportDialogOpen(true)}
            disabled={facilitiesWithSchemes === 0 || schemeSummaryLoading}
            sx={{ ml: 2, minWidth: '180px' }}
          >
            Export Scheme PDFs
          </Button>
        </Box>

        <TabPanel value={subTabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <FacilitiesTable
              sectionTitle="Facilities"
              showCollapseToggle={false}
              facilities={facilities}
              facilitiesWithSchemeData={facilitiesWithSchemeData}
              facilitiesData={facilitiesData}
              facilitiesLoading={facilitiesLoading}
              facilitiesSearch={facilitiesSearch}
              facilitiesStateFilter={facilitiesStateFilter}
              facilitiesPage={facilitiesPage}
              facilitiesPageSize={facilitiesPageSize}
              sortField={sortField}
              sortDirection={sortDirection}
              expandedFacilitiesScheme={expandedFacilitiesScheme}
              expandedSchemeInstances={expandedSchemeInstances}
              highlightedOwner={highlightedOwner}
              owners={owners}
              states={states}
              ownershipChangesData={ownershipChangesData}
              onSearchChange={onSearchChange}
              onStateFilterChange={onStateFilterChange}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
              onSort={onSort}
              onFacilitySchemeExpand={onFacilitySchemeExpand}
              onSchemeInstanceExpand={onSchemeInstanceExpand}
              onOwnerPillClick={onOwnerPillClick}
              onAddFacility={onAddFacility}
              onDeleteFacility={onDeleteFacility}
              formatMoney={formatMoney}
            />
          </Box>
        </TabPanel>

        <TabPanel value={subTabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <OwnersTable
              owners={owners}
              facilities={facilities}
              expandedOwners={expandedOwners}
              onOwnerExpand={onOwnerExpand}
              formatMoney={formatMoney}
            />
          </Box>
        </TabPanel>
      </Paper>

      {/* Export Scheme PDFs Dialog */}
      <ExportSchemePDFsDialog
        open={exportDialogOpen}
        onClose={() => {
          if (!exporting) {
            setExportDialogOpen(false);
          }
        }}
        onConfirm={handleExportConfirm}
        facilitiesCount={facilitiesWithSchemes}
        loading={exporting}
      />
    </Box>
  );
};